import { SidebarBoxKey } from 'AdmissionsGuidance';
import { useCurrentUser, useDateHelper } from 'FrontRoyalAngular';
import { ProgramTypeConfigs } from 'Program';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCohort, getReapplicationDate } from 'Users';
import { BasicMobileBox } from '../../BasicMobileBox';
import { BasicDesktopBox } from '../../BasicDesktopBox';
import { LogoBox } from '../../LogoBox';

function NotJoiningProgramCannotReapplyBoxComponent({ mobile }: { mobile?: boolean }) {
    const currentUser = useCurrentUser();
    const dateHelper = useDateHelper();
    const cohort = useMemo(() => currentUser && getCohort(currentUser), [currentUser]);
    const programTitle = useMemo(() => {
        if (!cohort) return null;
        const config = ProgramTypeConfigs[cohort.programType];
        return config.programBoxTitle?.(cohort) ?? config.fullTitle(cohort);
    }, [cohort]);
    const reapplicationDate = useMemo(() => {
        if (!currentUser) return null;
        const _reapplicationDate = getReapplicationDate(currentUser);
        if (!_reapplicationDate) return null;
        return dateHelper.formattedUserFacingMonthDayLong(_reapplicationDate, false);
    }, [currentUser, dateHelper]);

    if (!programTitle || !reapplicationDate) return null;

    return mobile ? (
        <NotJoiningProgramCannotReapplyBoxMobilePresentation
            programTitle={programTitle}
            reapplicationDate={reapplicationDate}
        />
    ) : (
        <NotJoiningProgramCannotReapplyBoxPresentation
            programTitle={programTitle}
            reapplicationDate={reapplicationDate}
        />
    );
}

function NotJoiningProgramCannotReapplyBoxPresentation({
    programTitle,
    reapplicationDate,
}: {
    programTitle: string;
    reapplicationDate: string;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'notJoiningProgramCannotReapplyBox' });
    return (
        <LogoBox title={programTitle} boxKey={SidebarBoxKey.not_joining_program_cannot_reapply}>
            {t('content', { reapplicationDate })}
        </LogoBox>
    );
}

function NotJoiningProgramCannotReapplyBoxMobilePresentation({
    programTitle,
    reapplicationDate,
}: {
    programTitle: string;
    reapplicationDate: string;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'notJoiningProgramCannotReapplyBox' });
    return (
        <BasicMobileBox
            heading={programTitle}
            boxKey={SidebarBoxKey.not_joining_program_cannot_reapply}
            ctaText={t('mobileButton')!}
            expandedContent={
                <BasicDesktopBox boxKey={SidebarBoxKey.not_joining_program_cannot_reapply} headerText={programTitle}>
                    {t('content', { reapplicationDate })}
                </BasicDesktopBox>
            }
        />
    );
}

export const NotJoiningProgramCannotReapplyBox = memo(NotJoiningProgramCannotReapplyBoxComponent);
